"use client";

import React, { useState, useEffect, useMemo } from 'react';
import { useTheme } from '@/components/ThemeContext';
import { 
  Folder, Laptop, Archive, Database, Clock, Send, Paperclip, 
  Search, Calendar, Maximize2, Minus, ChevronRight, Zap, Calculator, Target, Info, Check, Plus,
  CheckCircle2, User, FileText, AlertTriangle
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// --- Types ---
type DockState = 'min' | 'mid' | 'max';

// --- Mock Data ---
const SCOPES = ['인테리어설계', '실시설계', '감리', '3D투시도', 'FF&E', '가구설계', '조명설계'];

export default function SnapPayAIEstimateDemo() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // State: layout
  const [dockState, setDockState] = useState<DockState>('min');
  const [dockTab, setDockTab] = useState('NAS');
  const [chatInput, setChatInput] = useState('');
  const [isScheduling, setIsScheduling] = useState(false);
  const [hasScheduled, setHasScheduled] = useState(false);
  
  // State: Form
  const [usePy, setUsePy] = useState(false);
  const [isPublic, setIsPublic] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [grossArea, setGrossArea] = useState('850');
  const [selectedScopes, setSelectedScopes] = useState<string[]>(['인테리어설계', '실시설계', '3D투시도']);
  
  // State: AI
  const [chatMessages, setChatMessages] = useState<{role: string; text: string; attachments: string[]; actions: string[]}[]>([
    { role: 'ai', text: '프로젝트 기본 정보를 입력하시거나, 과거 파일을 우측 하단에서 첨부해 주시면 견적 초안을 자동으로 생성해 드립니다.', attachments: [], actions: [] }
  ]);

  // Calculations
  const pyArea = (Number(grossArea) * 0.3025).toFixed(1);
  const manhours = selectedScopes.length * 48; // mock
  const estimatedPrice = manhours * 65000 + (isPublic ? 1500000 : 0);

  const toggleDock = () => {
    if (dockState === 'min') setDockState('mid');
    else if (dockState === 'mid') setDockState('max');
    else setDockState('min');
  };

  const handleAttachMockFile = (fileName: string) => {
    setChatMessages(prev => [
      ...prev,
      { role: 'user', text: `참조 파일 확인해 줘.`, attachments: [fileName], actions: [] },
      { role: 'ai', text: `${fileName} 분석 완료. 해당 과거 프로젝트는 850㎡ 기준 평당 540,000원에 계약되었습니다. 현재 단가 인상분을 반영하여 **555,000원**으로 반영하는 것을 추천합니다.`, attachments: [], actions: ['견적에 반영', '단가 DB 추가'] }
    ]);
  };

  return (
    <div className={`relative h-[calc(100vh-120px)] grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_420px] grid-rows-[1fr_auto] gap-0 text-slate-900 dark:text-slate-200 overflow-hidden`}>
      
      {/* ① 좌측 메인 캔버스 */}
      <main className="row-start-1 col-start-1 overflow-y-auto bg-slate-50 dark:bg-[#080b12] relative">
        <div className="px-6 lg:px-10 py-8 max-w-[960px] space-y-6">
          
          {/* 1. Identity */}
          <section className="rounded-2xl border border-slate-200/70 dark:border-white/[0.06] bg-white dark:bg-slate-900/60 p-6 shadow-sm">
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-lg font-bold flex items-center gap-2"><Target className="w-5 h-5 text-indigo-500" /> 프로젝트 기초 정보</h2>
              <label className="flex items-center gap-2 cursor-pointer text-sm font-medium">
                 <input type="checkbox" checked={isPublic} onChange={e => setIsPublic(e.target.checked)} className="w-4 h-4 rounded accent-indigo-600" />
                 공공 프로젝트 (15% 가산)
              </label>
            </div>
            
            <div className="space-y-4">
               <div>
                <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">프로젝트명</label>
                <div className="flex gap-2">
                  <input type="text" value={projectName} onChange={e => setProjectName(e.target.value)} placeholder="서울 강남 상업시설 인테리어..." 
                    className="flex-1 h-10 rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/60 px-3 text-sm focus-visible:ring-2 ring-blue-500/60 ring-offset-0 outline-none" />
                  <button onClick={() => {
                     setProjectName('강남 리테일 A 매장 리뉴얼');
                     setGrossArea('1200');
                     setSelectedScopes(['인테리어설계', '실시설계']);
                     alert('📨 이메일 텍스트 파싱 결과로 폼 값이 갱신되었습니다.');
                  }} className="h-10 px-4 rounded-lg bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-sm font-semibold hover:bg-slate-200 dark:hover:bg-white/10 transition-colors">받은편지함에서 불러오기 ▾</button>
                </div>
              </div>

              <div className="grid grid-cols-4 gap-4">
                <div>
                   <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">유형</label>
                   <select className="w-full h-10 rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/60 px-3 text-sm outline-none"><option>상업</option><option>오피스</option></select>
                </div>
                <div>
                   <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">지역</label>
                   <select className="w-full h-10 rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/60 px-3 text-sm outline-none"><option>서울 강남권</option></select>
                </div>
                <div>
                   <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">용도 세분</label>
                   <select className="w-full h-10 rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/60 px-3 text-sm outline-none"><option>리테일·카페</option></select>
                </div>
                <div>
                   <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">규모 분류</label>
                   <select className="w-full h-10 rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/60 px-3 text-sm outline-none"><option>소규모 ≤1,000㎡</option></select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 pt-2 border-t border-slate-100 dark:border-white/5">
                <div>
                   <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">프로젝트 코드 (자동)</label>
                   <div className="h-10 flex items-center px-3 font-mono font-bold text-slate-400">ID_2026_024</div>
                </div>
                <div>
                   <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">계약 예정일</label>
                   <input type="date" value="2026-05-01" readOnly className="w-full h-10 rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/60 px-3 text-sm outline-none font-mono tabular-nums" />
                </div>
                <div>
                   <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">착수 희망일</label>
                   <input type="date" value="2026-05-15" readOnly className="w-full h-10 rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/60 px-3 text-sm outline-none font-mono tabular-nums" />
                </div>
              </div>
            </div>
          </section>

          {/* 2. Area Block */}
          <section className="rounded-2xl border border-slate-200/70 dark:border-white/[0.06] bg-white dark:bg-slate-900/60 p-6 shadow-sm">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-bold flex items-center gap-2"><Calculator className="w-5 h-5 text-emerald-500" /> 설계 대상 면적</h2>
              <div className="flex items-center gap-2 bg-slate-100 dark:bg-white/5 p-1 rounded-lg">
                <button onClick={() => setUsePy(false)} className={`px-3 py-1.5 rounded text-xs font-bold transition-all ${!usePy ? 'bg-white dark:bg-slate-800 shadow-sm text-emerald-600 dark:text-emerald-400' : 'text-slate-500'}`}>㎡ 단위</button>
                <button onClick={() => setUsePy(true)} className={`px-3 py-1.5 rounded text-xs font-bold transition-all ${usePy ? 'bg-white dark:bg-slate-800 shadow-sm text-emerald-600 dark:text-emerald-400' : 'text-slate-500'}`}>평 단위</button>
              </div>
            </div>

            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="relative">
                <label className="block text-[11px] font-semibold text-slate-500 dark:text-slate-400 mb-1.5">대지면적</label>
                <input type="text" inputMode="numeric" disabled value="1200" className="w-full font-mono tabular-nums h-10 bg-slate-50 border-slate-200 dark:bg-slate-800 dark:border-slate-700 rounded-lg px-3 text-sm" />
                <span className="absolute right-3 top-[26px] text-xs text-slate-400">㎡</span>
              </div>
              <div className="relative">
                <label className="block text-[11px] font-semibold text-slate-500 dark:text-slate-400 mb-1.5">건축면적</label>
                <input type="text" inputMode="numeric" disabled value="650" className="w-full font-mono tabular-nums h-10 bg-slate-50 border-slate-200 dark:bg-slate-800 dark:border-slate-700 rounded-lg px-3 text-sm" />
                <span className="absolute right-3 top-[26px] text-xs text-slate-400">㎡</span>
              </div>
              <div className="relative">
                <label className="block text-[11px] font-bold text-indigo-600 dark:text-indigo-400 mb-1.5">연면적 (기준)</label>
                <input type="text" inputMode="numeric" value={grossArea} onChange={e => setGrossArea(e.target.value)} className="w-full font-mono tabular-nums font-bold h-10 border border-indigo-200 dark:border-indigo-500/50 bg-white dark:bg-slate-900/60 rounded-lg px-3 text-sm focus-visible:ring-2 ring-indigo-500" />
                <span className="absolute right-3 top-[26px] text-xs font-bold text-indigo-400">㎡</span>
              </div>
              <div className="relative">
                <label className="block text-[11px] font-semibold text-slate-500 dark:text-slate-400 mb-1.5">운영 층수</label>
                <input type="text" inputMode="numeric" value="3" readOnly className="w-full font-mono tabular-nums h-10 bg-white border border-slate-200 dark:bg-slate-900/60 dark:border-slate-700 rounded-lg px-3 text-sm" />
                <span className="absolute right-3 top-[26px] text-xs text-slate-400">층</span>
              </div>
            </div>

            <div className="border border-slate-200 dark:border-white/10 rounded-xl overflow-hidden">
              <div className="bg-slate-50 dark:bg-white/5 p-3 flex justify-between items-center border-b border-slate-200 dark:border-white/10">
                <h3 className="text-xs font-bold">설계 대상 면적 세분화 (실제 과업)</h3>
                <button className="text-xs font-semibold text-blue-600 dark:text-blue-400 flex items-center gap-1"><Plus className="w-3 h-3"/> 영역 추가</button>
              </div>
              <div className="p-4 space-y-3">
                <div className="flex gap-4 items-center">
                  <input className="w-32 h-9 rounded bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 px-3 text-sm font-semibold" value="1F 로비·카페" readOnly />
                  <div className="relative w-32">
                    <input className="w-full h-9 rounded bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 px-3 text-sm font-mono tabular-nums" value="220" readOnly />
                    <span className="absolute right-3 top-2.5 text-[10px] text-slate-400">㎡</span>
                  </div>
                  <select className="flex-1 h-9 rounded bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 px-3 text-xs outline-none"><option>리테일 단가 100% 적용</option></select>
                </div>
                <div className="flex gap-4 items-center">
                  <input className="w-32 h-9 rounded bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 px-3 text-sm font-semibold" value="2F 오피스" readOnly />
                  <div className="relative w-32">
                    <input className="w-full h-9 rounded bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 px-3 text-sm font-mono tabular-nums" value="380" readOnly />
                    <span className="absolute right-3 top-2.5 text-[10px] text-slate-400">㎡</span>
                  </div>
                  <select className="flex-1 h-9 rounded bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 px-3 text-xs outline-none"><option>업무공간 단가 100% 적용</option></select>
                </div>
                <div className="flex gap-4 items-center">
                  <input className="w-32 h-9 rounded bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 px-3 text-sm font-semibold" value="3F 테라스·공용" readOnly />
                  <div className="relative w-32">
                    <input className="w-full h-9 rounded bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 px-3 text-sm font-mono tabular-nums" value="250" readOnly />
                    <span className="absolute right-3 top-2.5 text-[10px] text-slate-400">㎡</span>
                  </div>
                  <select className="flex-1 h-9 rounded bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 px-3 text-xs outline-none text-emerald-600 dark:text-emerald-400 font-semibold"><option>공용부 계수 70% 가중치 적용</option></select>
                </div>
              </div>
              <div className="bg-slate-50 dark:bg-[#0a0d14] p-4 flex justify-between items-center border-t border-slate-200 dark:border-white/10">
                <span className="text-sm font-bold">합계 (환산 설계면적)</span>
                <div className="text-right">
                  <div className="text-lg font-bold font-mono tabular-nums">{ grossArea } <span className="text-sm font-normal">㎡</span></div>
                  <div className="text-xs text-slate-500 font-mono tabular-nums">평당 환산 { pyArea } 평</div>
                </div>
              </div>
            </div>
            <label className="flex items-center justify-end gap-2 mt-3 cursor-pointer text-xs font-medium text-slate-500">
               <input type="checkbox" className="w-3.5 h-3.5 rounded accent-slate-600" /> 실내 전용면적 별도 분리기준 적용
            </label>
          </section>

          {/* 3. Scope & Deliverables */}
          <section className="rounded-2xl border border-slate-200/70 dark:border-white/[0.06] bg-white dark:bg-slate-900/60 p-6 shadow-sm">
            <h2 className="text-lg font-bold flex items-center gap-2 mb-4"><CheckCircle2 className="w-5 h-5 text-blue-500" /> 과업 범위 및 성과품</h2>
            <div className="flex flex-wrap gap-2 mb-6">
              {SCOPES.map(s => {
                const isSel = selectedScopes.includes(s);
                return (
                  <button key={s} onClick={() => setSelectedScopes(p => isSel ? p.filter(x=>x!==s) : [...p, s])}
                    className={`px-4 py-2 rounded-full text-sm font-semibold transition-all border ${isSel ? 'bg-blue-600 border-blue-600 text-white shadow-sm dark:bg-blue-500 dark:border-blue-500' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-700'}`}>
                    {isSel && <Check className="w-3.5 h-3.5 inline mr-1" strokeWidth={3}/>} {s}
                  </button>
                )
              })}
              <button className="px-4 py-2 rounded-full text-sm font-medium border border-dashed border-slate-300 text-slate-500 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-400 dark:hover:bg-slate-800">
                ＋ 커스텀 추가
              </button>
            </div>

            <div className="border border-slate-200 outline-none dark:border-white/10 rounded-xl overflow-hidden">
               <div className="bg-slate-50 dark:bg-white/5 p-3 flex justify-between items-center border-b border-slate-200 dark:border-white/10">
                <h3 className="text-xs font-bold">▣ 선택된 성과품 (Deliverables) Checklist</h3>
                <button className="text-xs font-semibold text-slate-500 bg-white dark:bg-slate-800 border border-slate-200 dark:border-white/10 px-2 py-1 rounded">템플릿 불러오기 ▾</button>
              </div>
              <div className="p-4 space-y-4">
                 {selectedScopes.includes('인테리어설계') && (
                   <div className="space-y-2">
                     <div className="flex items-center justify-between group">
                       <label className="flex items-center gap-2 text-sm font-medium"><input type="checkbox" defaultChecked className="accent-indigo-500 rounded"/> 기본설계 도면집 (A3, 1부)</label>
                       <div className="text-xs font-mono tabular-nums text-slate-400 flex items-center gap-2">맨아워 <input className="w-12 h-6 text-center border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-900" defaultValue="48"/> h</div>
                     </div>
                     <div className="flex items-center justify-between group">
                       <label className="flex items-center gap-2 text-sm font-medium"><input type="checkbox" defaultChecked className="accent-indigo-500 rounded"/> 피난 방재 검토서</label>
                       <div className="text-xs font-mono tabular-nums text-slate-400 flex items-center gap-2">맨아워 <input className="w-12 h-6 text-center border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-900" defaultValue="16"/> h</div>
                     </div>
                   </div>
                 )}
                 {selectedScopes.includes('실시설계') && (
                   <div className="space-y-2 pt-4 border-t border-slate-100 dark:border-white/5">
                     <div className="flex items-center justify-between group">
                       <label className="flex items-center gap-2 text-sm font-medium"><input type="checkbox" defaultChecked className="accent-indigo-500 rounded"/> 실시설계 도면집 (A1, 2부)</label>
                       <div className="text-xs font-mono tabular-nums text-slate-400 flex items-center gap-2">맨아워 <input className="w-12 h-6 text-center border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-900" defaultValue="80"/> h</div>
                     </div>
                     <div className="flex items-center justify-between group">
                       <label className="flex items-center gap-2 text-sm font-medium"><input type="checkbox" defaultChecked className="accent-indigo-500 rounded"/> 재료 마감재 시방서</label>
                       <div className="text-xs font-mono tabular-nums text-slate-400 flex items-center gap-2">맨아워 <input className="w-12 h-6 text-center border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-900" defaultValue="24"/> h</div>
                     </div>
                   </div>
                 )}
                 {selectedScopes.includes('3D투시도') && (
                   <div className="space-y-2 pt-4 border-t border-slate-100 dark:border-white/5">
                     <div className="flex items-center justify-between group">
                       <label className="flex items-center gap-2 text-sm font-medium"><input type="checkbox" defaultChecked className="accent-indigo-500 rounded"/> 주요 공간 3D 렌더링 (6컷)</label>
                       <div className="text-xs font-mono tabular-nums text-slate-400 flex items-center gap-2">맨아워 <input className="w-12 h-6 text-center border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-900" defaultValue="24"/> h</div>
                     </div>
                   </div>
                 )}
                 {(!selectedScopes.includes('인테리어설계') && !selectedScopes.includes('실시설계') && !selectedScopes.includes('3D투시도')) && (
                   <div className="py-4 text-center text-sm text-slate-400 italic">왼쪽에서 과업 범위를 선택하시면 성과품 목록이 나타납니다.</div>
                 )}
                 <div className="pt-2"><button className="text-xs font-bold text-blue-500 flex items-center gap-1"><Plus className="w-3 h-3"/> 수동 항목 추가</button></div>
              </div>
              <div className="bg-slate-50 dark:bg-slate-800/40 p-3 text-center text-xs font-semibold border-t border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-400">
                성과품 요약: 필수 5건 · 예상 총 맨아워 <span className="font-mono tabular-nums text-blue-600 dark:text-blue-400 font-bold ml-1">{manhours} h</span>
              </div>
            </div>
          </section>

          {/* 4. Manhour Plan */}
          <section className="rounded-2xl border border-slate-200/70 dark:border-white/[0.06] bg-white dark:bg-slate-900/60 p-6 shadow-sm">
             <h2 className="text-lg font-bold flex items-center gap-2 mb-4"><User className="w-5 h-5 text-orange-500" /> 인력 구성 및 맨아워 집계</h2>
             <div className="grid lg:grid-cols-2 gap-6">
               <div className="border border-slate-200 dark:border-white/10 rounded-xl overflow-hidden">
                 <table className="w-full text-xs text-left">
                   <thead className="bg-slate-50 dark:bg-white/5 border-b border-slate-200 dark:border-white/10">
                     <tr><th className="p-2.5 font-semibold">역할</th><th className="p-2.5 font-semibold">급수</th><th className="p-2.5 font-semibold">투입</th><th className="p-2.5 text-right font-semibold">H단가(원)</th></tr>
                   </thead>
                   <tbody className="divide-y divide-slate-100 dark:divide-white/[0.04]">
                     <tr><td className="p-2.5 font-bold">PM</td><td className="p-2.5">수석</td><td className="p-2.5 font-mono">30%</td><td className="p-2.5 text-right font-mono text-slate-500">80,000</td></tr>
                     <tr><td className="p-2.5 font-bold">책임설계</td><td className="p-2.5">책임</td><td className="p-2.5 font-mono">100%</td><td className="p-2.5 text-right font-mono text-slate-500">65,000</td></tr>
                     <tr><td className="p-2.5 font-bold">실무설계</td><td className="p-2.5">대리</td><td className="p-2.5 font-mono">100%</td><td className="p-2.5 text-right font-mono text-slate-500">45,000</td></tr>
                   </tbody>
                 </table>
               </div>
               <div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-16 text-xs font-bold text-slate-600 dark:text-slate-400">기본설계</div>
                      <div className="flex-1 h-3 bg-slate-100 dark:bg-slate-800 rounded-full"><div className="h-full bg-blue-500 rounded-full w-[40%]"></div></div>
                      <div className="w-10 text-right text-xs font-mono font-semibold">48h</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-16 text-xs font-bold text-slate-600 dark:text-slate-400">실시설계</div>
                      <div className="flex-1 h-3 bg-slate-100 dark:bg-slate-800 rounded-full"><div className="h-full bg-indigo-500 rounded-full w-[70%]"></div></div>
                      <div className="w-10 text-right text-xs font-mono font-semibold">80h</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-16 text-xs font-bold text-slate-600 dark:text-slate-400">투시도</div>
                      <div className="flex-1 h-3 bg-slate-100 dark:bg-slate-800 rounded-full"><div className="h-full bg-cyan-500 rounded-full w-[20%]"></div></div>
                      <div className="w-10 text-right text-xs font-mono font-semibold">24h</div>
                    </div>
                  </div>
                  <div className="mt-4 pt-3 border-t border-slate-200 dark:border-slate-800 flex justify-between items-center text-sm font-bold">
                    <span>총 인건비 예상액</span>
                    <span className="font-mono tabular-nums text-lg text-emerald-600 dark:text-emerald-400">{(manhours * 65000).toLocaleString()} 원</span>
                  </div>
               </div>
             </div>
          </section>

          {/* 5. Schedule Gantt */}
          <section className="rounded-2xl border border-slate-200/70 dark:border-white/[0.06] bg-white dark:bg-slate-900/60 p-6 shadow-sm">
             <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg font-bold flex items-center gap-2"><Calendar className="w-5 h-5 text-rose-500" /> 과업 기간 (Schedule)</h2>
                <button 
                  onClick={() => {
                     setIsScheduling(true);
                     setTimeout(() => {
                       setIsScheduling(false);
                       setHasScheduled(true);
                       setChatMessages(prev => [...prev, {role: 'ai', text: '📅 최적 일정이 재계산되었습니다. 기본설계 일정이 W4 → W3로 앞당겨졌습니다. 과업 기간(Schedule) 차트를 확인해 주세요.', attachments: [], actions: []}]);
                     }, 2000);
                  }}
                  disabled={isScheduling}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-violet-100 text-violet-700 hover:bg-violet-200 dark:bg-violet-500/20 dark:text-violet-300 transition-colors shadow-sm border border-violet-200 dark:border-violet-500/30 disabled:opacity-50">
                  {isScheduling ? <div className="w-3.5 h-3.5 rounded-full border-2 border-violet-500 border-t-transparent animate-spin"/> : <Zap className="w-3.5 h-3.5 fill-violet-500" />} 
                  {isScheduling ? '스케줄 추론 중...' : '자동 스케줄링 (AI)'}
                </button>
             </div>
             
              <div className="border border-slate-200 dark:border-white/10 rounded-xl overflow-hidden p-4 bg-slate-50 dark:bg-slate-900 overflow-x-auto relative">
                {isScheduling && <div className="absolute inset-0 bg-white/50 dark:bg-slate-900/50 backdrop-blur-[2px] z-10 flex items-center justify-center font-bold text-violet-600 dark:text-violet-400 font-mono">추론 중... (API/Estimator/Schedule)</div>}
                <div className="min-w-[600px]">
                  <div className="flex text-[10px] font-bold text-slate-400 mb-2 border-b border-slate-200 dark:border-slate-800 pb-2">
                    <div className="w-24">Task</div>
                    {[1,2,3,4,5,6,7,8,9,10].map(w => <div key={w} className="flex-1 text-center">W{w}</div>)}
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center text-xs font-semibold">
                       <div className="w-24">기본설계</div>
                       <div className={`flex-1 rounded h-4 border transition-all duration-1000 ${hasScheduled ? 'bg-indigo-500/80 mr-[50%] border-indigo-600/50' : 'bg-blue-500/80 mr-[60%] border-blue-600/50'}`}></div>
                    </div>
                    <div className="flex items-center text-xs font-semibold">
                       <div className="w-24">실시설계</div>
                       <div className={`flex-1 rounded h-4 border transition-all duration-1000 ${hasScheduled ? 'bg-blue-500/80 ml-[30%] mr-[30%] border-blue-600/50' : 'bg-indigo-500/80 ml-[20%] mr-[40%] border-indigo-600/50'}`}></div>
                    </div>
                    <div className="flex items-center text-xs font-semibold">
                       <div className="w-24">3D 투시도</div>
                       <div className={`flex-1 rounded h-4 border transition-all duration-1000 ${hasScheduled ? 'bg-cyan-500/80 ml-[60%] mr-[20%] border-cyan-600/50' : 'bg-cyan-500/80 ml-[40%] mr-[40%] border-cyan-600/50'}`}></div>
                    </div>
                   <div className="flex items-center text-xs font-semibold">
                       <div className="w-24">고객 검수</div>
                       <div className={`flex-1 rounded h-4 border transition-all duration-1000 ${hasScheduled ? 'bg-amber-500/80 ml-[80%] mr-[10%] border-amber-600/50' : 'bg-amber-500/80 ml-[80%] border-amber-600/50'}`}></div>
                    </div>
                  </div>
                </div>
             </div>
             <p className="text-xs text-center text-slate-500 mt-3 font-medium">총 10주 (2026-05-15 ~ 2026-07-24)</p>
          </section>

          {/* Padding bottom for floating logic/scroll */}
          <div className="h-20" />
        </div>

        {/* 6. Sticky Live Estimate Card */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
          className="absolute top-8 right-8 w-64 rounded-2xl bg-white dark:bg-[#0a0f1c] border border-blue-200 dark:border-blue-500/30 shadow-[0_8px_30px_rgb(0,0,0,0.12)] dark:shadow-[0_8px_30px_rgba(59,130,246,0.15)] flex flex-col overflow-hidden z-20">
          <div className="bg-blue-600 dark:bg-blue-600 px-4 py-2 flex justify-between items-center text-white">
            <span className="text-xs font-bold flex items-center gap-1.5"><Zap className="w-3.5 h-3.5 fill-white"/> AI 엔진 산출 결과</span>
          </div>
          <div className="p-5">
            <div className="text-xs font-semibold text-slate-500 mb-1">예상 총 설계비 (VAT 별도)</div>
            <motion.div key={estimatedPrice} initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="text-2xl font-black font-mono tabular-nums text-slate-900 dark:text-white">
               {estimatedPrice.toLocaleString()} <span className="text-sm font-normal text-slate-500">원</span>
            </motion.div>
            
            <div className="mt-4 pt-4 border-t border-slate-100 dark:border-white/10 space-y-2 text-xs">
              <div className="flex justify-between"><span className="text-slate-500">설계 면적</span><span className="font-mono tabular-nums font-semibold">{grossArea} ㎡</span></div>
              <div className="flex justify-between"><span className="text-slate-500">평당 단가</span><span className="font-mono tabular-nums font-semibold">{Math.round(estimatedPrice / Number(pyArea)).toLocaleString()} 원/평</span></div>
              <div className="flex justify-between"><span className="text-slate-500">예상 인력</span><span className="font-mono tabular-nums font-semibold text-blue-600 dark:text-blue-400">{manhours} h</span></div>
            </div>
          </div>
        </motion.div>
      </main>

      {/* ② 우측 AI 챗봇 패널 */}
      <aside className="hidden xl:flex flex-col w-[420px] shrink-0 border-l border-slate-200 dark:border-white/[0.06] bg-slate-50/60 dark:bg-slate-950/40 backdrop-blur z-20 h-full">
        <header className="px-5 py-4 border-b border-slate-200 dark:border-white/[0.06] flex items-center gap-3 bg-white/50 dark:bg-slate-900/50">
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-60" />
            <span className="rounded-full h-2.5 w-2.5 bg-emerald-500" />
          </span>
          <h2 className="text-[15px] font-bold tracking-tight">SnapPay 견적 에이전트</h2>
          <span className="ml-auto text-[11px] font-bold text-slate-400 flex items-center gap-1"><Database className="w-3 h-3"/> 208건 학습됨</span>
        </header>

        <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-4">
          
          {/* Context Block */}
          <div className="rounded-xl bg-white dark:bg-slate-900 shadow-sm border border-slate-200 dark:border-white/10 p-4">
            <h3 className="text-[11px] font-bold text-indigo-500 flex items-center gap-1 mb-2">🧠 실시간 폼 컨텍스트</h3>
            <div className="text-[13px] font-medium leading-relaxed">
              <span className="text-slate-900 dark:text-white bg-slate-100 dark:bg-slate-800 px-1 rounded">{projectName || '프로젝트 무제'}</span> 입력 중.<br/>
              서울 강남권 상업시설, 면적 <span className="font-mono">{grossArea}㎡</span> 기반으로 분석하고 있습니다.
            </div>
          </div>

          {/* Dynamic Suggestion Block */}
          <div className="rounded-xl bg-indigo-50 dark:bg-indigo-500/10 shadow-sm border border-indigo-100 dark:border-indigo-500/20 p-4 relative overflow-hidden">
             <div className="absolute top-0 right-0 p-3 opacity-10"><Zap className="w-12 h-12" /></div>
             <h3 className="text-[11px] font-bold text-indigo-600 dark:text-indigo-400 flex items-center gap-1 mb-3">💡 능동 제안</h3>
             <div className="space-y-4">
                <div>
                  <div className="text-xs font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-1.5"><Folder className="w-3.5 h-3.5"/> 관련된 과거 유사 프로젝트 3건</div>
                  <ul className="mt-2 space-y-1.5 ml-5 text-[11px] text-slate-600 dark:text-slate-400 list-disc marker:text-indigo-300">
                    <li className="cursor-pointer hover:text-indigo-600 transition-colors">강남 리테일 A 매장 (2024년)</li>
                    <li className="cursor-pointer hover:text-indigo-600 transition-colors">성수 복합문화공간 B (2025년)</li>
                    <li className="cursor-pointer hover:text-indigo-600 transition-colors">청담 프리미엄 쇼룸 (2025년)</li>
                  </ul>
                  <button onClick={() => {
                    setChatMessages(prev => [...prev, {role: 'user', text: '세 건 모두 단가 분포 참조하기', attachments: [], actions: []}, {role: 'ai', text: '네, 과거 3건의 유사 프로젝트 단가 분포를 기반으로, 예상 평당 단가는 적정 530,000원입니다. 단가 DB에 곧장 반영했습니다.', attachments: [], actions: ['견적에 반영', '상세 가중치 보기']}]);
                  }} className="mt-2 text-[10px] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-2 py-1 rounded shadow-sm hover:shadow transition-all font-bold">세 건 모두 단가 분포 참조하기</button>
                </div>
                {selectedScopes.includes('인테리어설계') && (
                  <div className="pt-3 border-t border-indigo-200/50 dark:border-indigo-500/20">
                    <div className="text-xs font-semibold text-rose-600 dark:text-rose-400 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5"/> 누락 의심 성과품 감지</div>
                    <p className="mt-1 text-[11px] text-slate-600 dark:text-slate-400 leading-tight">강남 상업시설 인허가 관련 **소방 방재 검토서**가 누락되었습니다.</p>
                    <button onClick={() => {
                       setChatMessages(prev => [...prev, {role: 'user', text: '체크리스트에 자동 추가해줘', attachments: [], actions: []}, {role: 'ai', text: '소방 방재 검토서(16h)를 성과품 목록에 추가 완료했습니다.', attachments: [], actions: []}]);
                    }} className="mt-2 text-[10px] bg-rose-50 dark:bg-rose-500/20 border border-rose-200 dark:border-rose-500/30 text-rose-700 dark:text-rose-300 px-2 py-1 rounded font-bold hover:bg-rose-100 dark:hover:bg-rose-500/30 transition-colors">체크리스트에 자동 추가</button>
                  </div>
                )}
             </div>
          </div>

          {/* Chat Messages */}
          <div className="flex-1 space-y-4 pt-4 mt-2 border-t border-slate-200 dark:border-white/10">
             {chatMessages.map((m, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] text-[13px] rounded-2xl px-4 py-3 shadow-sm ${m.role === 'user' ? 'bg-slate-800 text-white rounded-br-sm' : 'bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 rounded-bl-sm space-y-3'}`}>
                    {m.text.includes('**') ? <span dangerouslySetInnerHTML={{__html: m.text.replace(/\*\*(.*?)\*\*/g, '<b class="text-blue-600 dark:text-blue-400">$1</b>')}} /> : m.text}
                    {m.attachments.length > 0 && (
                      <div className="flex flex-col gap-2 mt-2 pt-2 border-t border-white/20">
                        {m.attachments.map(att => (
                           <div key={att} className="text-[11px] font-mono bg-black/20 px-2 py-1 rounded flex items-center gap-1.5"><FileText className="w-3 h-3"/> {att}</div>
                        ))}
                      </div>
                    )}
                    {m.actions.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-slate-100 dark:border-slate-800">
                        {m.actions.map(act => (
                          <button key={act} className="text-[11px] font-bold bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-500/30 px-2.5 py-1.5 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-500/20 transition-colors">
                            {act}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </motion.div>
             ))}
          </div>

        </div>

               {/* Composer */}
        <div className="p-4 bg-white/80 dark:bg-slate-900/80 backdrop-blur border-t border-slate-200 dark:border-white/[0.06]">
           <div className="flex gap-2 mb-3 px-1 overflow-x-auto no-scrollbar">
              <button onClick={() => setChatInput('NAS 파일들을 문서 콘텍스트로 참부해줘.')} className="shrink-0 flex items-center gap-1 text-[10px] font-semibold bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10 px-2 py-1 rounded-full text-slate-600 dark:text-slate-300 transition-colors"><Paperclip className="w-3 h-3"/> NAS 첨부</button>
              <button onClick={() => setChatInput('위 추천 결과를 바탕으로 시장 단가표를 조회해.')} className="shrink-0 flex items-center gap-1 text-[10px] font-semibold bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10 px-2 py-1 rounded-full text-slate-600 dark:text-slate-300 transition-colors"><Search className="w-3 h-3"/> 단가 조회</button>
              <button onClick={() => {
                setChatInput('입력된 폼 정보를 통해 WBS 일정을 자동생성해.');
                setIsScheduling(true);
                setTimeout(() => {
                  setIsScheduling(false);
                  setHasScheduled(true);
                  setChatMessages(prev => [...prev, {role: 'user', text: '입력된 폼 정보를 통해 WBS 일정을 자동생성해.', attachments: [], actions: []}, {role: 'ai', text: '📅 최적 일정이 재계산되었습니다. 전체 10주 일정으로 단축되었으며, 기본설계 일정이 W4 → W3로 앞당겨졌습니다. 과업 기간(Schedule) 차트를 확인해 주세요.', attachments: [], actions: []}]);
                }, 2000);
              }} className="shrink-0 flex items-center gap-1 text-[10px] font-semibold bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10 px-2 py-1 rounded-full text-slate-600 dark:text-slate-300 transition-colors"><Calendar className="w-3 h-3"/> 일정 자동생성</button>
           </div>
           <div className="relative">
             <input type="text" placeholder="질문이나 지시를 입력하세요 (예: 단가 낮춰줘)..." 
               value={chatInput} onChange={(e) => setChatInput(e.target.value)}
               onKeyDown={(e) => {
                 if (e.key === 'Enter' && chatInput.trim()) {
                   const qs = chatInput;
                   let reply = '네, 실시간 컨텍스트를 기반 분석 결과입니다.';
                   if (qs.includes('단가') && qs.includes('낮춰')) {
                     reply = '현재 시장 단가 대비 10% 낮춘 조정안을 적용했습니다. 내역서 탭의 단가가 갱신되었습니다.';
                   } else if (qs.includes('일정') || qs.includes('스케줄')) {
                     reply = '요청하신 정보대로 일정을 조정했습니다. 과업 기간(Schedule) 차트에 반영되었습니다.';
                   } else if (qs.includes('첨부') || qs.includes('NAS')) {
                     reply = '해당 NAS 폴더의 최근 문서들을 컨텍스트로 불러왔습니다.';
                   }
                   setChatMessages(prev => [...prev, {role: 'user', text: qs, attachments: [], actions: []}, {role: 'ai', text: reply, attachments: [], actions: []}]);
                   setChatInput('');
                 }
               }}
               className="w-full bg-slate-50 dark:bg-black/40 border border-slate-200 dark:border-white/10 rounded-xl pl-4 pr-10 py-3 text-sm focus-visible:ring-2 ring-indigo-500 outline-none placeholder:text-slate-400" />
             <button onClick={() => {
                 if (chatInput.trim()) {
                   setChatMessages(prev => [...prev, {role: 'user', text: chatInput, attachments: [], actions: []}, {role: 'ai', text: '네, 확인했습니다.', attachments: [], actions: []}]);
                   setChatInput('');
                 }
             }} className="absolute right-2 top-2 w-8 h-8 flex items-center justify-center bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"><Send className="w-4 h-4 ml-0.5"/></button>
           </div>
        </div>
      </aside>

      {/* ③ 하단 리소스 독 */}
      <section className={`row-start-2 col-span-full border-t border-slate-200 dark:border-white/[0.06] bg-white/90 dark:bg-slate-900/80 backdrop-blur-md transition-all duration-300 shadow-[0_-8px_30px_-10px_rgba(0,0,0,0.1)] dark:shadow-[0_-8px_30px_-10px_rgba(0,0,0,0.5)] z-30 flex flex-col ${
        dockState === 'min' ? 'h-10' : dockState === 'mid' ? 'h-64' : 'h-[420px]'
      }`}>
        <header className="h-10 px-4 flex items-center shrink-0 cursor-pointer select-none group border-b border-slate-100 dark:border-white/5" onClick={toggleDock}>
          <div className="flex gap-1 h-full pt-1.5">
            {['NAS', '로컬 PC', '과거 프로젝트', '단가 DB', '최근 열람'].map(t => (
              <button key={t} onClick={(e) => { e.stopPropagation(); setDockTab(t); if(dockState==='min') setDockState('mid'); }}
                className={`px-4 py-1.5 rounded-t-lg text-xs font-bold transition-colors ${dockTab === t ? 'bg-slate-100 text-indigo-600 dark:bg-white/10 dark:text-indigo-400' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}`}>
                {t}
              </button>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-2 text-slate-400 opacity-50 group-hover:opacity-100 transition-opacity">
            <button onClick={(e) => { e.stopPropagation(); setDockState('max'); }} title="크게 보기" className="hover:text-slate-800 dark:hover:text-white p-1"><Maximize2 className="w-3.5 h-3.5" /></button>
            <button onClick={(e) => { e.stopPropagation(); setDockState('min'); }} title="최소화" className="hover:text-slate-800 dark:hover:text-white p-1"><Minus className="w-3.5 h-3.5" /></button>
          </div>
        </header>
        
        <div className="flex-1 overflow-auto p-4 custom-scrollbar">
          {dockState !== 'min' && (
             <AnimatePresence mode="wait">
               {dockTab === 'NAS' && (
                 <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex h-full gap-4">
                   <div className="w-64 border-r border-slate-200 dark:border-white/10 pr-4 shrink-0 overflow-y-auto">
                     <p className="text-[11px] font-bold text-slate-400 mb-2 font-mono">/Volumes/DesignNAS/Projects/2024/강남_리테일_A/</p>
                     <ul className="space-y-1.5 text-sm font-medium">
                       <li className="flex items-center gap-2 text-slate-500 cursor-pointer hover:bg-slate-50 dark:hover:bg-white/5 px-2 py-1 rounded"><Folder className="w-4 h-4 fill-amber-400 text-amber-500"/> 01_기획안</li>
                       <li className="flex items-center gap-2 text-slate-800 dark:text-slate-200 bg-slate-100 dark:bg-white/10 cursor-pointer px-2 py-1 rounded"><Folder className="w-4 h-4 fill-amber-400 text-amber-500"/> 02_견적서 및 산출</li>
                       <li className="flex items-center gap-2 text-slate-500 cursor-pointer hover:bg-slate-50 dark:hover:bg-white/5 px-2 py-1 rounded"><Folder className="w-4 h-4 fill-amber-400 text-amber-500"/> 03_디자인도면</li>
                       <li className="flex items-center gap-2 text-slate-500 cursor-pointer hover:bg-slate-50 dark:hover:bg-white/5 px-2 py-1 rounded"><Folder className="w-4 h-4 fill-amber-400 text-amber-500"/> 04_3D렌더링</li>
                     </ul>
                   </div>
                   <div className="flex-1 flex flex-col gap-2">
                     {[
                       {n: '최종_견적서_강남리테일A_V3.pdf', s: '1.2 MB', d: '2024-08-12', i: <FileText className="w-4 h-4 text-rose-500"/>},
                       {n: '수량산출내역서_종합.xlsx', s: '340 KB', d: '2024-08-10', i: <Database className="w-4 h-4 text-emerald-500"/>},
                       {n: '단가_비교표_비공개.xlsx', s: '128 KB', d: '2024-08-11', i: <Database className="w-4 h-4 text-emerald-500"/>},
                     ].map((f, i) => (
                       <div key={i} className="flex items-center justify-between p-3 rounded-xl border border-slate-100 hover:border-slate-300 dark:border-white/5 dark:hover:border-white/20 bg-slate-50 dark:bg-black/20 transition-all group">
                         <div className="flex items-center gap-3">
                           {f.i} 
                           <span className="text-sm font-semibold">{f.n}</span>
                           <span className="text-[10px] font-mono tabular-nums text-slate-400 bg-slate-200/50 dark:bg-slate-800 px-1.5 py-0.5 rounded">{f.s}</span>
                         </div>
                         <div className="flex items-center gap-4 opacity-0 group-hover:opacity-100 transition-opacity">
                           <span className="text-[11px] font-mono tabular-nums text-slate-500">{f.d}</span>
                           <div className="flex items-center gap-2">
                             <button className="text-xs font-semibold px-3 py-1.5 rounded bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:shadow-sm">프리뷰</button>
                             <button onClick={() => handleAttachMockFile(f.n)} className="text-xs font-bold px-3 py-1.5 rounded bg-indigo-50 text-indigo-600 hover:bg-indigo-100 dark:bg-indigo-500/20 dark:text-indigo-300 dark:hover:bg-indigo-500/30 transition-colors">참조하여 분석하기 ➔</button>
                           </div>
                         </div>
                       </div>
                     ))}
                   </div>
                 </motion.div>
               )}
               {dockTab === '과거 프로젝트' && (
                 <div className="h-full flex items-center justify-center text-sm font-semibold text-slate-400 bg-slate-50/50 dark:bg-slate-900/50 border border-slate-200/50 dark:border-white/5 rounded-xl m-2 italic">
                   현재 입력된 '상업/서울/850㎡' 조건과 일치하는 12건의 과거 프로젝트 뷰가 렌더됩니다 (개발 중)
                 </div>
               )}
               {['로컬 PC', '단가 DB', '최근 열람'].includes(dockTab) && (
                 <div className="h-full flex items-center justify-center text-sm font-semibold text-slate-400 bg-slate-50/50 dark:bg-slate-900/50 border border-slate-200/50 dark:border-white/5 rounded-xl m-2 italic">
                   {dockTab} 데이터 모듈 연결 중입니다 (개발 중)
                 </div>
               )}
             </AnimatePresence>
          )}
        </div>
      </section>

    </div>
  );
}
